import React, { useEffect, useRef, useState, forwardRef, useImperativeHandle } from 'react';
import moment from 'moment';
import { today, yesterday } from  '@/helpers/Dates';
import { IDateRange, IOption } from  '@/interfaces/insightsp2';
import CustomSelect from './CustomSelect';
import Calendar from 'react-calendar';

type Props ={
    setDateRange: ({dateFrom, dateTo}: {dateFrom:Date, dateTo:Date}) => void,
    reset?:boolean,
    setReset?:(val:boolean) => void,
    defaultDate?:IOption,
    showDateNum?:boolean,
    placeholder?:string
}

const DateRange = forwardRef(({setDateRange,  reset,  defaultDate, placeholder}: Props, forwardRef) => {

    const [mainDate, setMainDate] = useState<IOption[]>(null);
    const [label, setLabel] = useState<string>("");
    const [customDate, setCustomDate] = useState<Date[]>(null);
    const [selectedDates, setSelectedDates] = useState<IDateRange>(null);
    const [showCalendar, setShowCalendar] = useState<boolean>(false);
    
    const customSelectRef = useRef(null);
  
    // Init
    useEffect(() => {

        // Set default date label
        if (defaultDate){
            setLabel(defaultDate.label);
        }
        
    }, [])

    // Show hide calendar
    useEffect(() => {

        // Close calendar if clicked away
        function clickListener(e:MouseEvent){

            if (!inCal(e) && customDate && customDate.length === 2){
                //setShowCalendar(false)
            }
        }

        window.addEventListener('click', clickListener); 

        return () => {
            window.removeEventListener('click', clickListener);
        }

    }, [customDate])

    // Selected date
    useEffect(() => {

        if (mainDate && mainDate.length === 1){

            const selectedDate = mainDate[0];

            // If custom stop and show calendar
            if (selectedDate.value === "custom"){
                setShowCalendar(true);
                return;
            }
   
            const convertedDates = getDateRange(selectedDate.value);

            setSelectedDates(convertedDates)

            if (convertedDates){
                setDateRange(convertedDates);
            }

            // Set label
            setLabel(convertedDates.label)

            // Reset custom date
            setCustomDate(null)
        }    

    }, [mainDate])
 
    // Custom date
    useEffect(() => {

        // Date range selected
        if(customDate && customDate.length === 2){
     
            const date1 = moment(customDate[0]).toDate();
            const date2 = moment(customDate[1]).toDate();

            const buildRange: IDateRange = {
              dateFrom:date1,
              dateTo:date2,
              label:"Custom"
            }

            setDateRange(buildRange);

            setLabel("Custom");

            setShowCalendar(false);
        }
  
    }, [customDate])

    // Reset
    useEffect(() => {

        if (reset){

        }

    },[reset])

    // On open
    useEffect(() => {

        if (showCalendar){
                   
            const button = document.querySelector('.react-calendar__navigation__label');

            if (button){
                button.addEventListener('click', (e) => {
                    e.stopPropagation();
                })
            }

        }

    },[showCalendar])

    // Expose function to clear from above
    useImperativeHandle(forwardRef, () => {
        return {
            clear: () => {

                // Clear/reset from above
                if (defaultDate) setLabel(defaultDate.label);

                // Clear below - this not ideal
                customSelectRef.current.clear();
            }
        };
    });

    // Close calendar
    function closeCal(){

        // If no date selected reset
        if(!customDate){
    
            if (defaultDate) setLabel(defaultDate.label);

            // Clear below - this not ideal
            customSelectRef.current.clear();
        }

        // If single date selected double dates and close
        if(customDate && customDate.length === 1){
            setCustomDate([customDate[0],customDate[0]]);
        }     

        setShowCalendar(false);
    }

    // Calculate date range from string
    function getDateRange(dateRangeSelected:string): IDateRange {

        if (!dateRangeSelected){
            return
        }

        let label = dateList.filter(x => x.value === dateRangeSelected)[0].label;

        let dateFrom;
        let dateTo;
    
        switch(dateRangeSelected) {
    
          case "today":
    
              dateFrom = moment().hour(12).toDate();
              dateTo = moment().hour(12).toDate();
              label = `Today (${today().dateFrom.toLocaleDateString()})`;
 
              break;
    
          case "yesterday":
    
              dateFrom = moment().subtract(1,'d').hour(12).toDate();
              dateTo = moment().subtract(1,'d').hour(12).toDate();
              label = `Yesterday (${yesterday().dateFrom.toLocaleDateString()})`;
              break;
    
          case "lastweek":
    
              dateFrom = moment().subtract(1,'w').startOf('week').add('day').hour(12).toDate();
              dateTo = moment().subtract(1,'w').endOf('week').hour(12).toDate();
              break;

          case "last7days":

              dateFrom = moment().subtract(7,'d').hour(12).toDate();
              dateTo = moment().subtract(1,'d').hour(12).toDate();
              break;
        
          case "lastmonth":
    
              dateFrom = moment().subtract(1,'month').startOf('month').hour(12).toDate();
              dateTo = moment().subtract(1,'month').endOf('month').hour(12).toDate();
              break;
        
          case "last3months":
              dateFrom = moment().subtract(3,'month').startOf('month').hour(12).toDate();
              dateTo = moment().subtract(1,'month').endOf('month').hour(12).toDate();
              break;
    
          case "lifetime":
            
              dateFrom = moment().subtract(3,'y').startOf('year').hour(12).toDate();
              dateTo = moment().subtract(1,'d').hour(12).toDate();
              break;
    
          default:
            
        }
    
        return { dateFrom, dateTo, label };
    }

    // Close calendar on click away
    function inCal(e):boolean {

        if (e.path){
            for (let i = 0; i < e.path.length; i++){  
                if (e.path[i].className === "react-calendar"){
                    return true;
                }  
            }
        }

        return false;
    }

    function defaultIndex():number{

        if (defaultDate && defaultDate.value === "yesterday"){
            return 1;
        }

        return 0;
    }

    function handleChange(value){
        if(value[0] && value[1]) {
            setCustomDate([new Date(value[0]), new Date(value[1])])
        }
    }

    // Date options
    const dateList:IOption[] = [
        { value: 'today', label: `Today` },
        { value: 'yesterday', label: `Yesterday` },
        { value: 'lastweek', label: 'Last Week' },
        { value: 'last7days', label: 'Last 7 Days' },
        { value: 'lastmonth', label: 'Last Month' },
        { value: 'last3months', label: 'Last 3 Months' },
        { value: 'lifetime', label: 'Lifetime' },
        { value: 'custom', label: 'Custom Date' }
    ];

    return (
        <div className="react-selector" >

            <CustomSelect 
                options={dateList} 
                optionsChange={setMainDate} 
                ref={customSelectRef} 
                multi={false} 
                label={label}
                customDate={customDate}
                selectedDates={selectedDates}
                showCalendar={showCalendar}
                showDateLabel={true}
                defaultSelectedIndex={defaultIndex()}
                placeholder={placeholder}
            />

            {showCalendar && 
                <div className="cal-container">
                    <Calendar
                        onChange={handleChange}
                        value={customDate as any}
                        selectRange={true}
                        allowPartialRange={true}
                        nextAriaLabel="Next"
                        prevAriaLabel="Previous"
                    />
                    <a className="close-cal" onClick={closeCal}>Close</a>
                </div>
            }

        </div>
    );
});

export default DateRange;